import { bilingual } from "./i18n";

// How far along a Director turn is, for the customer: the stages it reached,
// in order, and an estimate of the share of the work done. The estimate is
// abstract by design (time and actions, eased so it always moves and never
// goes back); reaching a late stage lifts it.

export type Stage =
  | "prepare"
  | "plan"
  | "product"
  | "voice"
  | "music"
  | "visuals"
  | "scenes"
  | "review"
  | "deliver";

export interface TurnProgress {
  startedAt: string;
  /** Making the project's first version; revisions take a fraction of it. */
  first: boolean;
  /** Stages in the order the Director first reached them. */
  steps: { stage: Stage; at: string }[];
  /** Actions taken so far. */
  work: number;
  /**
   * It waited in line for a workspace. Its clock then starts once it has one,
   * and the customer hears by email when its version is ready if they are
   * not watching.
   */
  waited?: boolean;
  endedAt?: string;
}

/** Where each stage usually starts in a first video. */
export const STAGE_AT: Record<Stage, number> = {
  prepare: 0,
  plan: 0.04,
  product: 0.1,
  voice: 0.2,
  music: 0.28,
  visuals: 0.36,
  scenes: 0.5,
  review: 0.7,
  deliver: 0.88,
};

type StageText = Record<Stage, { doing: string; done: string; noun: string }>;
/** What each stage is called while it runs, once it is behind, and on a bill. */
export const STAGES = bilingual<StageText>({
  en: {
    prepare: {
      doing: "Preparing your studio",
      done: "Studio ready",
      noun: "Studio setup",
    },
    plan: {
      doing: "Planning the video",
      done: "Planned the video",
      noun: "Planning",
    },
    product: {
      doing: "Studying your product",
      done: "Studied your product",
      noun: "Product research",
    },
    voice: {
      doing: "Recording the voiceover",
      done: "Recorded the voiceover",
      noun: "Voiceover",
    },
    music: {
      doing: "Mixing the soundtrack",
      done: "Mixed the soundtrack",
      noun: "Soundtrack",
    },
    visuals: {
      doing: "Preparing the visuals",
      done: "Prepared the visuals",
      noun: "Visuals",
    },
    scenes: {
      doing: "Animating the scenes",
      done: "Animated the scenes",
      noun: "Animation",
    },
    review: {
      doing: "Reviewing the cut",
      done: "Reviewed the cut",
      noun: "Review",
    },
    deliver: {
      doing: "Finishing your preview",
      done: "Preview ready",
      noun: "Finishing",
    },
  },
  es: {
    prepare: {
      doing: "Preparando tu estudio",
      done: "Estudio listo",
      noun: "Preparación del estudio",
    },
    plan: {
      doing: "Planificando el vídeo",
      done: "Vídeo planificado",
      noun: "Planificación",
    },
    product: {
      doing: "Estudiando tu producto",
      done: "Producto estudiado",
      noun: "Estudio del producto",
    },
    voice: {
      doing: "Grabando la locución",
      done: "Locución grabada",
      noun: "Locución",
    },
    music: {
      doing: "Mezclando la banda sonora",
      done: "Banda sonora mezclada",
      noun: "Banda sonora",
    },
    visuals: {
      doing: "Preparando las imágenes",
      done: "Imágenes preparadas",
      noun: "Imágenes",
    },
    scenes: {
      doing: "Animando las escenas",
      done: "Escenas animadas",
      noun: "Animación",
    },
    review: {
      doing: "Revisando el montaje",
      done: "Montaje revisado",
      noun: "Revisión",
    },
    deliver: {
      doing: "Terminando tu vista previa",
      done: "Vista previa lista",
      noun: "Acabado",
    },
  },
});

/** A typical first video: about 18 minutes and 60 actions. */
const MINUTES = 18,
  ACTIONS = 60;

/** The share of the work done, 0–0.97 while it runs. */
export function estimate(p: TurnProgress, now = Date.now()) {
  const minutes = Math.max(0, now - Date.parse(p.startedAt)) / 60_000,
    scale = p.first ? 1 : 0.3,
    x =
      (0.5 * minutes) / (MINUTES * scale) + (0.5 * p.work) / (ACTIONS * scale);
  let v = 0.95 * (1 - Math.exp(-1.6 * x));
  for (const s of p.steps)
    if (p.first || s.stage === "deliver") v = Math.max(v, STAGE_AT[s.stage]);
  return Math.min(0.97, v);
}
